/**
 * Helper per la visualizzazione degli allegati media V13 degli eventi
 * (icon-strip nella card homepage, rendering inline nella pagina dettaglio).
 */

import type { EventoMediaItemDto, EventoMediaType } from "@/lib/api";

export interface MediaTypeMeta {
  /** Emoji/icona UTF per il badge */
  icon: string;
  /** Etichetta singolare */
  label: string;
  /** Etichetta al plurale (per conteggi > 1) */
  labelPlural: string;
  /** Tinta Tailwind per pill/badge */
  pill: string;
}

export const MEDIA_TYPE_META: Record<EventoMediaType, MediaTypeMeta> = {
  VIDEO_URL:  { icon: "🎬", label: "Video",      labelPlural: "Video",      pill: "bg-red-50 text-red-700 border-red-200" },
  VIDEO_FILE: { icon: "🎥", label: "Video",      labelPlural: "Video",      pill: "bg-red-50 text-red-700 border-red-200" },
  PHOTO:      { icon: "📷", label: "Foto",       labelPlural: "Foto",       pill: "bg-amber-50 text-amber-700 border-amber-200" },
  GALLERY:    { icon: "🖼",  label: "Galleria",   labelPlural: "Galleria",   pill: "bg-amber-50 text-amber-700 border-amber-200" },
  PDF:        { icon: "📄", label: "PDF",        labelPlural: "PDF",        pill: "bg-rose-50 text-rose-700 border-rose-200" },
  SLIDE:      { icon: "📊", label: "Slide",      labelPlural: "Slide",      pill: "bg-violet-50 text-violet-700 border-violet-200" },
  NOTE:       { icon: "📝", label: "Nota",       labelPlural: "Note",       pill: "bg-sky-50 text-sky-700 border-sky-200" },
  PRESS_URL:  { icon: "📰", label: "Articolo",   labelPlural: "Articoli",   pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  PRESS_FILE: { icon: "🗞", label: "Ritaglio",   labelPlural: "Ritagli",    pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

/**
 * Raggruppa gli items per "categoria visiva":
 *  - video (VIDEO_URL + VIDEO_FILE)
 *  - foto/gallery
 *  - pdf
 *  - slide
 *  - note
 *  - stampa (PRESS_URL + PRESS_FILE)
 * Usato per l'icon-strip della card: troppi badge distinti sarebbero rumorosi.
 */
export type MediaGroupKey = "video" | "foto" | "pdf" | "slide" | "nota" | "stampa";

const GROUP_MAP: Record<EventoMediaType, MediaGroupKey> = {
  VIDEO_URL:  "video",
  VIDEO_FILE: "video",
  PHOTO:      "foto",
  GALLERY:    "foto",
  PDF:        "pdf",
  SLIDE:      "slide",
  NOTE:       "nota",
  PRESS_URL:  "stampa",
  PRESS_FILE: "stampa",
};

export const GROUP_META: Record<MediaGroupKey, MediaTypeMeta> = {
  video:  { icon: "🎬", label: "Video",    labelPlural: "Video",    pill: "bg-red-50 text-red-700 border-red-200" },
  foto:   { icon: "🖼",  label: "Foto",     labelPlural: "Foto",     pill: "bg-amber-50 text-amber-700 border-amber-200" },
  pdf:    { icon: "📄", label: "PDF",      labelPlural: "PDF",      pill: "bg-rose-50 text-rose-700 border-rose-200" },
  slide:  { icon: "📊", label: "Slide",    labelPlural: "Slide",    pill: "bg-violet-50 text-violet-700 border-violet-200" },
  nota:   { icon: "📝", label: "Note",     labelPlural: "Note",     pill: "bg-sky-50 text-sky-700 border-sky-200" },
  stampa: { icon: "📰", label: "Stampa",   labelPlural: "Stampa",   pill: "bg-emerald-50 text-emerald-700 border-emerald-200" },
};

export interface MediaGroup {
  key: MediaGroupKey;
  count: number;
  meta: MediaTypeMeta;
}

export function groupMediaItems(items: EventoMediaItemDto[] | null | undefined): MediaGroup[] {
  if (!items?.length) return [];
  const counts = new Map<MediaGroupKey, number>();
  for (const it of items) {
    const k = GROUP_MAP[it.mediaType];
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const order: MediaGroupKey[] = ["video", "foto", "pdf", "slide", "nota", "stampa"];
  return order
    .filter((k) => (counts.get(k) ?? 0) > 0)
    .map((k) => ({ key: k, count: counts.get(k)!, meta: GROUP_META[k] }));
}

